/**
 * The `$PRES` icon picker, in a popup rather than inline: 23 chips sitting in
 * the middle of the depth section was exactly the clutter this exists to
 * remove. Same `<dialog>` idiom as `FeedbackDialog` — this one just has no
 * form or network behind it.
 */

import { useRef } from "react";

import { PRESET_ICON_LABELS, PRESET_ICON_VALUES } from "../lib/conditions";
import { PresetIconGlyph } from "./PresetIconGlyph";

interface IconPickerDialogProps {
  open: boolean;
  value: number;
  onChange: (icon: number) => void;
  onClose: () => void;
}

export function IconPickerDialog({ open, value, onChange, onClose }: IconPickerDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const handleOpen = (el: HTMLDialogElement | null) => {
    dialogRef.current = el;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  };

  return (
    <dialog
      ref={handleOpen}
      onClose={onClose}
      className="m-auto w-[min(94vw,420px)] rounded-[3px] border-2 border-graphite bg-paper p-0 text-graphite backdrop:bg-graphite/50"
    >
      <div className="border-b border-slate-line px-4 py-3">
        <h2 className="text-[22px] font-bold uppercase leading-none">Dial icon</h2>
        <p className="mt-1 text-[13px] text-graphite-2">
          Shown next to the name on the camera's own preset picker.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-[6px] p-3.5" role="group" aria-label="Dial icon">
        {PRESET_ICON_VALUES.map((icon) => {
          const pressed = icon === value;
          return (
            <button
              key={icon}
              type="button"
              aria-pressed={pressed}
              onClick={() => {
                onChange(icon);
                onClose();
              }}
              className={[
                "flex cursor-pointer flex-col items-center gap-1 rounded-[2px] border-[1.5px] border-graphite px-1.5 py-2",
                "transition-colors",
                pressed
                  ? "bg-graphite text-paper"
                  : "bg-transparent text-graphite hover:bg-graphite/10",
              ].join(" ")}
            >
              <PresetIconGlyph icon={icon} className="h-6 w-6" />
              <span className="font-display text-[10px] font-semibold uppercase leading-tight tracking-[0.04em]">
                {PRESET_ICON_LABELS[icon]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex justify-end border-t border-slate-line px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite"
        >
          Close
        </button>
      </div>
    </dialog>
  );
}
